import { useZero } from "@rocicorp/zero/react";
import { useMemo } from "react";
import { sharedMutators } from "../../../../shared/mutators";
import { queries } from "../../../../shared/queries";
import type { ScopeKind } from "../../../../shared/rbac/permissions";
import {
  BUILTIN_ROLES,
  type BuiltinRoleId,
} from "../../../../shared/rbac/roles";
import { useGroupMemberships } from "../../../hooks/useGroupMemberships";
import { useHasAccess } from "../../../hooks/useHasAccess";
import { useQuery } from "../../../hooks/useQuery";
import { useResolveScopeName } from "../../../hooks/useResolveScopeName";
import {
  buildRoleRows,
  filterVisibleScopes,
  findGroupsForRole,
  findServiceAccountsForRole,
  findUsersForRole,
} from "./lib";
import { InspectRoleModal } from "./InspectRoleModal";

export function InspectRoleModalById({
  roleId,
  onClose,
}: {
  roleId: string;
  onClose: () => void;
}) {
  const z = useZero();
  const canManageRoles = useHasAccess("tenant:roles_manage");
  const [customRoles] = useQuery(queries.allRolesLite());
  const [allUserGroups] = useQuery(queries.allUserGroups());
  const { memberCounts: groupMemberCounts } = useGroupMemberships();
  const [allUsers] = useQuery(queries.allUsers());
  const [allDirectUserGrantsRaw] = useQuery(queries.allDirectUserGrants());
  const [allDirectServiceAccountGrants] = useQuery(
    queries.allDirectServiceAccountGrants()
  );
  const { resolveScopeName, personalSpaceIds } = useResolveScopeName();

  const allDirectUserGrants = useMemo(
    () =>
      allDirectUserGrantsRaw.filter((g) => !personalSpaceIds.has(g.scopeId)),
    [allDirectUserGrantsRaw, personalSpaceIds]
  );

  const userMap = useMemo(() => {
    const map = new Map<string, (typeof allUsers)[number]>();
    for (const u of allUsers) map.set(u.id, u);
    return map;
  }, [allUsers]);

  const allRoleRows = useMemo(
    () => buildRoleRows(customRoles, new Map()),
    [customRoles]
  );

  const row = allRoleRows.find((r) => r.id === roleId);
  const builtinFallback = !row
    ? BUILTIN_ROLES[roleId as BuiltinRoleId]
    : undefined;
  if (!row && !builtinFallback) return null;

  const role = row
    ? {
        id: row.id,
        name: row.name,
        scopeKind: row.scope,
        permissions: row.permissions,
        isBuiltin: row.isBuiltin,
      }
    : {
        id: roleId,
        name: builtinFallback!.label,
        scopeKind: builtinFallback!.scopeKind as ScopeKind,
        permissions: builtinFallback!.permissions,
        isBuiltin: true,
      };

  const assignedGroups =
    role.scopeKind !== "user_group"
      ? filterVisibleScopes(
          findGroupsForRole(
            allUserGroups,
            role.id,
            role.scopeKind,
            role.isBuiltin
          ),
          role.scopeKind,
          resolveScopeName
        )
      : undefined;

  const assignedUsers = filterVisibleScopes(
    findUsersForRole(allDirectUserGrants, role.id, role.isBuiltin, userMap),
    role.scopeKind,
    resolveScopeName
  );

  const assignedServiceAccounts = filterVisibleScopes(
    findServiceAccountsForRole(
      allDirectServiceAccountGrants,
      role.id,
      role.isBuiltin
    ),
    role.scopeKind,
    resolveScopeName
  );

  return (
    <InspectRoleModal
      role={role}
      onClose={onClose}
      onDelete={(roleId) => {
        z.mutate(sharedMutators.deleteRole({ roleId }));
        onClose();
      }}
      canManage={canManageRoles}
      assignedGroups={assignedGroups}
      assignedUsers={assignedUsers}
      assignedServiceAccounts={assignedServiceAccounts}
      groupMemberCounts={groupMemberCounts}
      resolveScopeName={resolveScopeName}
    />
  );
}
